import {Theme, useTheme} from '@mui/material'
import {CSSObject, createMakeStyles, Css, Cx, createWithStyles} from 'tss-react'
import {OcTheme} from './OcTheme'
import {MuiThemeStyleOverridesPluginParams} from 'tss-react/mui/themeStyleOverridesPlugin'

export const withStyles = createWithStyles({useTheme}).withStyles

function makeStylesWithParams<T extends Theme = Theme, Params = void, RuleNamesForSubselectors extends string = never>(
    name?: string
) {
    return createMakeStyles<T>({useTheme}).makeStyles<Params, RuleNamesForSubselectors>({name})
}

type StylesOrStylesFunction<
    T extends Theme,
    Params,
    RuleName extends string,
    RuleNamesForSubselectors extends string
> =
    | Record<RuleName, CSSObject>
    | ((
          theme: T,
          params: Params,
          classes: Record<RuleNamesForSubselectors, string>
      ) => Record<RuleName | RuleNamesForSubselectors, CSSObject>)

type UseStyles<T extends Theme, Params, RuleName extends string> = (
    params: Params,
    muiStyleOverridesParams?: MuiThemeStyleOverridesPluginParams['muiStyleOverridesParams']
) => {
    classes: Record<RuleName, string>
    theme: T
    css: Css
    cx: Cx
}

export function makeStyles<
    Params = void,
    RuleName extends string = string,
    RuleNamesForSubselectors extends string = never
>(styles: StylesOrStylesFunction<Theme, Params, RuleName, RuleNamesForSubselectors>): UseStyles<Theme, Params, RuleName>
export function makeStyles<
    Params = void,
    RuleName extends string = string,
    RuleNamesForSubselectors extends string = never
>(
    name: string,
    styles: StylesOrStylesFunction<Theme, Params, RuleName, RuleNamesForSubselectors>
): UseStyles<Theme, Params, RuleName>
export function makeStyles<
    Params = void,
    RuleName extends string = string,
    RuleNamesForSubselectors extends string = never
>(
    stylesOrName: StylesOrStylesFunction<Theme, Params, RuleName, RuleNamesForSubselectors> | string,
    stylesOrNothing?: StylesOrStylesFunction<Theme, Params, RuleName, RuleNamesForSubselectors>
): UseStyles<Theme, Params, RuleName> {
    if (typeof stylesOrName === 'string' && typeof stylesOrNothing !== 'undefined') {
        return makeStylesWithParams<Theme, Params, RuleNamesForSubselectors>(stylesOrName)(stylesOrNothing)
    }

    if (typeof stylesOrName !== 'string') {
        return makeStylesWithParams<Theme, Params, RuleNamesForSubselectors>()(stylesOrName)
    }

    throw new Error('Invalid overload arguments')
}

/**
 * MUI's `makeStyles` typed with `OcTheme`.
 *
 * @warning This doesn't actually enforce that OC theme will be provided. The hook still have to be rendered in child component of `OcThemeProvider`.
 */
export function makeOcStyles<
    Params = void,
    RuleName extends string = string,
    RuleNamesForSubselectors extends string = never
>(styles: StylesOrStylesFunction<OcTheme, Params, RuleName, RuleNamesForSubselectors>) {
    return makeStylesWithParams<OcTheme, Params, RuleNamesForSubselectors>()(styles)
}
